import { ref, computed, watch, onBeforeUnmount } from "vue";
import moment from "moment";
import engine from "../core/DataApi";

// ─────────────────────────────────────────────────────────────────────────
// Module « Logs Web » — logique (state + actions)
// But : visualiser / suivre ce qui se passe sur les différents sites web à
// partir de la table FileMaker WEB_LOGS.
// Tout le front est prêt ; le back (endpoints /api/web-logs/...) est à
// finaliser. Le chargement est tolérant : il accepte plusieurs formes de
// réponse FileMaker et refiltre côté client.
// ─────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────
// Sites proposés dans le menu déroulant de sélection.
// 👉 ÉDITE cette liste pour ajouter/retirer tes sites.
// (Quand l'endpoint back get_logs_sites sera prêt, ses sites seront
//  automatiquement fusionnés avec ceux-ci.)
// ─────────────────────────────────────────────────────────────────────────

export default function () {
    // Données brutes chargées depuis le back (déjà aplaties)
    const logs = ref([]);
    const isLoading = ref(false);
    // Liste des sites — UNIQUEMENT ceux renvoyés par get_logs_sites.
    const siteList = ref([]);
    const isSitesLoading = ref(false);
    const sitesError = ref(false);

    // Filtres de l'interface
    const filters = ref({
        search: "",
        niveau: [],      // multi-sélection (INFO / WARN / ERROR / DEBUG)
        statut: "",      // texte / code HTTP (ex : 200, 404, 500)
        site: null,      // hôte extrait du path (ex : velo-test2.accen.me)
        client: null,    // client (IP)
        user: null,      // utilisateur
        typeEvenement: [], // type d'événement (api / page / événements front…)
        parcours: null,  // id_parcours = session de l'utilisateur
        limit: 250,      // nombre de lignes à charger (chargement rapide : 250 par défaut)
        periode: "1h",   // preset de période par défaut (5m, 1h, 1j… ou "custom")
        dateFrom: null,  // timestamp (DatePicker) début
        dateTo: null,    // timestamp (DatePicker) fin
    });

    // Nombre de lignes chargées par défaut au 1er affichage d'un site.
    const DEFAULT_LIMIT = 250;
    // Période par défaut (fenêtre glissante).
    const DEFAULT_PERIODE = "1h";
    // Entrée spéciale du filtre User : regroupe les appels SANS utilisateur.
    const UNKNOWN_USER = "(Utilisateur inconnu)";
    // Durées des presets de période, en millisecondes.
    const MIN = 60000, HOUR = 3600000, DAY = 86400000;
    const PERIODE_MS = {
        "5m": 5 * MIN, "10m": 10 * MIN, "15m": 15 * MIN, "30m": 30 * MIN,
        "1h": HOUR, "2h": 2 * HOUR, "3h": 3 * HOUR, "6h": 6 * HOUR,
        "1d": DAY, "2d": 2 * DAY, "7d": 7 * DAY, "14d": 14 * DAY,
        "1M": 30 * DAY, "3M": 90 * DAY, "6M": 180 * DAY,
    };

    // Détail (modale)
    const selectedLog = ref(null);
    const isDetailOpen = ref(false);

    // Rafraîchissement automatique (ancien mode, conservé mais non utilisé)
    const autoRefresh = ref(false);

    // Mode LIVE : récupère en continu les NOUVELLES lignes (selon les filtres),
    // les fusionne en tête SANS recharger tout le tableau (aucun écran de
    // chargement, aucun saut). Intervalle de rafraîchissement.
    const isLive = ref(false);
    const LIVE_INTERVAL_MS = 1000;
    // Ids des lignes tout juste arrivées → sert au petit "flash" visuel.
    const newIds = ref([]);

    // Retour visuel
    const snackbar = ref({ show: false, text: "", color: "" });
    const showMessage = function (text, color) {
        snackbar.value = { show: true, text: text, color: color };
    };

    // ── Normalisation des réponses FileMaker ────────────────────────────────
    // Selon l'endpoint, FileMaker renvoie soit un tableau direct, soit
    // data.data.data[] (objets { fieldData, recordId }). On gère les deux.
    const extractRows = function (data) {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        const candidates = [
            data.records,
            data.rows,
            data.data,
            data.data && data.data.data,
            data.data && data.data.data && data.data.data.data,
        ];
        for (let i = 0; i < candidates.length; i++) {
            if (Array.isArray(candidates[i])) return candidates[i];
        }
        return [];
    };

    // Transforme une ligne FileMaker en objet log « plat » et homogène.
    // Récupère l'horodatage de création quel que soit le nom exact du champ
    // FileMaker (accents/encodage/variantes). On cherche d'abord des noms
    // connus, puis on repère un champ « horodatage/date de création » en
    // excluant la modification.
    // Champs de contenu du log : ne JAMAIS les prendre pour un horodatage
    // (le champ "response"/"body" contient un gros JSON qui « ressemble »
    // parfois à une date).
    const TS_BLOCKLIST = [
        "request", "statut", "body", "client", "user", "response", "path",
        "duree_ms", "niveau", "id_correlation", "code_fm", "erreur_message",
        "Clé primaire", "cle_primaire", "Créé par", "cree_par", "Modifié par", "modifie_par",
    ];
    // Valeur qui ressemble à une vraie date/heure, et COURTE (pas un JSON).
    const looksLikeDate = (v) =>
        typeof v === "string" && v.length <= 40 &&
        /(\d{4}-\d{1,2}-\d{1,2}|\d{1,2}\/\d{1,2}\/\d{2,4})/.test(v);

    // Normalise un nom de champ : supprime les accents (gère "é" précomposé vs
    // décomposé renvoyé par FileMaker), met en minuscules, enlève les espaces.
    const norm = (s) => (s || "").toString().normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

    const pickTimestamp = function (fd, wantModification) {
        if (!fd) return "";
        // Match par TOKENS (ignore espaces normaux/insécables, accents, casse) :
        // le champ « Horodatage de création » = contient "horodat" ET "crea".
        const nk = Object.keys(fd).find((k) => {
            const n = norm(k);
            return wantModification
                ? (n.indexOf("horodat") !== -1 && n.indexOf("modif") !== -1)
                : (n.indexOf("horodat") !== -1 && (n.indexOf("creat") !== -1 || n.indexOf("crea") !== -1));
        });
        if (nk && fd[nk] !== undefined && fd[nk] !== null && fd[nk] !== "") return fd[nk];

        const explicit = wantModification
            ? ["Horodatage de modification", "horodatage_modification", "date_modification", "modified_at", "updatedAt"]
            : ["Horodatage de création", "horodatage_creation", "Horodatage_creation", "date_creation",
               "Date de création", "created_at", "createdAt", "creation_timestamp"];
        for (let i = 0; i < explicit.length; i++) {
            const v = fd[explicit[i]];
            if (v !== undefined && v !== null && v !== "") return v;
        }
        const keys = Object.keys(fd).filter((k) => TS_BLOCKLIST.indexOf(k) === -1);
        const isModif = (k) => /modif/i.test(k);
        const wanted = (k) => wantModification ? isModif(k) : !isModif(k);
        // 1) nom évoquant une date de création/modification + valeur date courte
        let k = keys.find((x) => wanted(x) && /(cr[ée]a|creat|modif|horodat|date)/i.test(x) && looksLikeDate(fd[x]));
        if (k) return fd[k];
        // 2) nom évoquant un horodatage (même si le format de valeur est inhabituel)
        k = keys.find((x) => wanted(x) && /(horodat|cr[ée]a|creat|modif)/i.test(x) && fd[x]);
        if (k) return fd[k];
        // 3) dernier recours : une valeur date COURTE dans un champ non-contenu
        k = keys.find((x) => wanted(x) && looksLikeDate(fd[x]));
        if (k) return fd[k];
        return "";
    };

    const mapRow = function (row, index) {
        const fd = row && row.fieldData ? row.fieldData : (row || {});
        const recordId = row && row.recordId ? row.recordId : null;
        // Horodatage : création si dispo, sinon modification (dernier recours).
        const ts = pickTimestamp(fd, false) || pickTimestamp(fd, true);
        return {
            id: fd["Clé primaire"] || fd.cle_primaire || recordId || index,
            recordId: recordId,
            horodatage: ts,
            cree_par: fd["Créé par"] || fd.cree_par || "",
            modifie_par: fd["Modifié par"] || fd.modifie_par || "",
            horodatage_modification: fd["Horodatage de modification"] || "",
            request: fd.request || "",
            statut: fd.statut !== undefined && fd.statut !== null ? String(fd.statut) : "",
            body: fd.body || "",
            client: fd.client || "",
            user: fd.user || "",
            response: fd.response || "",
            path: fd.path || "",
            duree_ms: fd.duree_ms !== undefined && fd.duree_ms !== null ? String(fd.duree_ms) : "",
            niveau: fd.niveau || "",
            id_correlation: fd.id_correlation || "",
            code_fm: fd.code_fm || "",
            erreur_message: fd.erreur_message || "",
            // Domaine (site) du log — champ dédié, fiable pour TOUS les types
            // (les page/selection ont un path = "/" qui ne contient pas le domaine).
            domaine: fd.domaine || fd.DOMAINE || "",
            // Session de l'utilisateur (nouvelle à chaque fermeture/réouverture)
            id_parcours: fd.id_parcours || fd.ID_PARCOURS || "",
            // Type d'événement (api, page, et événements front : clic bouton…)
            type_evenement: fd.type_evenement || fd.TYPE_EVENEMENT || "",
            _raw: fd,
        };
    };

    const normalizeRecords = function (data) {
        const rows = extractRows(data);
        return rows.map(mapRow);
    };

    // ── Chargement ──────────────────────────────────────────────────────────
    const loadLogs = function () {
        isLoading.value = true;
        newIds.value = []; // on repart propre : pas de surlignage hérité
        // On envoie les filtres au back (il pourra en tenir compte plus tard) ;
        // le front refiltre de toute façon via filteredLogs.
        engine.get_logs(buildBackendFilters(), function (data) {
            isLoading.value = false;
            console.log("get_logs", data);
            if (data && data.code === 0) {
                logs.value = normalizeRecords(data);
            } else if (data && data.code === -1) {
                // Endpoint pas encore dispo : on tente le "tout charger".
                loadLogsAll();
            } else {
                logs.value = normalizeRecords(data);
            }
        });
    };

    // Repli : charge tous les logs sans filtre serveur.
    const loadLogsAll = function () {
        isLoading.value = true;
        engine.get_logs_all(function (data) {
            isLoading.value = false;
            console.log("get_logs_all", data);
            if (data && data.code === -1) {
                showMessage("Impossible de charger les logs (back-end non disponible).", "error");
                logs.value = [];
                return;
            }
            logs.value = normalizeRecords(data);
        });
    };

    // Prépare l'objet de filtres (recherche SERVEUR) envoyé au back.
    // Contrat back-end attendu :
    //   - site      : hôte à charger (filtre sur le champ path).
    //   - limit     : nombre MAX de lignes à renvoyer, triées par date décroissante
    //                 (les plus récentes d'abord). 250 par défaut = chargement rapide.
    //   - dateFrom / dateTo : bornes de période en millisecondes (null = pas de borne).
    //                 Si une période est fournie, le back filtre sur l'horodatage de
    //                 création ; sinon il renvoie simplement les `limit` plus récentes.
    //   - client / user / statut / niveau / search : filtres optionnels additionnels.
    const buildBackendFilters = function () {
        const f = filters.value;
        // Nombre de lignes demandé (chargement rapide : 250 par défaut).
        let lim = parseInt(f.limit);
        if (isNaN(lim) || lim <= 0) lim = DEFAULT_LIMIT;
        // Bornes de période → timestamps ms (fin de journée incluse côté "fin").
        const fromD = parseDate(f.dateFrom);
        const toD = parseDate(f.dateTo);
        if (toD) toD.setHours(23, 59, 59, 999);
        let fromMs = fromD ? fromD.getTime() : null;
        let toMs = toD ? toD.getTime() : null;
        // Preset de période = fenêtre GLISSANTE depuis maintenant (recalculée à
        // chaque requête). "custom" utilise les dates choisies ; null = pas de
        // borne de période.
        if (f.periode && f.periode !== "custom" && PERIODE_MS[f.periode]) {
            fromMs = Date.now() - PERIODE_MS[f.periode];
            toMs = null;
        }
        // La PÉRIODE prime sur le nombre de lignes : dès qu'une fenêtre est
        // active, on demande TOUT ce qui est dedans (plafond de sécurité), le
        // "Nb lignes" est ignoré. Sans période, on garde le "Nb lignes".
        const hasWindow = fromMs != null || toMs != null;
        if (hasWindow) lim = 10000;
        const payload = {
            site: f.site || "",
            limit: lim,
            search: f.search || "",
            niveau: f.niveau || [],
            statut: f.statut || "",
            client: f.client || "",
            user: f.user === UNKNOWN_USER ? "" : (userCode(f.user) || ""),
            typeEvenement: f.typeEvenement || [],
            parcours: f.parcours || "",
            dateFrom: fromMs,
            dateTo: toMs,
        };
        console.log("buildBackendFilters", payload);
        return payload;
    };

    const refresh = function () {
        if (filters.value.site) loadLogs();
    };

    // Réinitialise les filtres SAUF le site (on reste sur le site chargé) et
    // recharge les 250 dernières lignes (recherche serveur) du site courant.
    const resetFilters = function () {
        const site = filters.value.site;
        filters.value = { search: "", niveau: [], statut: "", site: site, client: null, user: null, typeEvenement: [], parcours: null, limit: DEFAULT_LIMIT, periode: DEFAULT_PERIODE, dateFrom: null, dateTo: null };
        if (site) loadLogs();
    };

    // Revient à l'écran de sélection du site (vide les logs).
    const changeSite = function () {
        logs.value = [];
        filters.value.site = null;
    };

    // Charge la liste des sites (UNIQUEMENT ceux renvoyés par get_logs_sites).
    const loadSites = function () {
        isSitesLoading.value = true;
        sitesError.value = false;
        engine.get_logs_sites(function (data) {
            isSitesLoading.value = false;
            console.log("get_logs_sites", data);
            if (data && data.code === -1) { sitesError.value = true; return; }
            // Format OPTIMISÉ : le back renvoie directement { sites: [...] }.
            const direct = (data && data.data && Array.isArray(data.data.sites)) ? data.data.sites
                : (Array.isArray(data && data.sites) ? data.sites : null);
            if (direct) {
                siteList.value = [...new Set(direct.filter((s) => s && s.length > 0))].sort();
                if (siteList.value.length === 0) sitesError.value = true;
                return;
            }
            // Repli : ancien format (enregistrements complets) → on dérive l'hôte.
            const rows = extractRows(data);
            const set = {};
            rows.forEach((r) => {
                const fd = r && r.fieldData ? r.fieldData : r;
                const s = typeof fd === "string"
                    ? fd
                    : ((fd && (fd.site || fd.SITE || fd.host)) || siteHost(fd && fd.path));
                if (s && s.length > 0) set[s] = true;
            });
            siteList.value = Object.keys(set).sort();
            if (siteList.value.length === 0) sitesError.value = true;
        });
    };

    // ── Détail ───────────────────────────────────────────────────────────────
    const openDetail = function (row) {
        selectedLog.value = row;
        isDetailOpen.value = true;
    };
    const closeDetail = function () {
        isDetailOpen.value = false;
    };

    // ── Suppression ───────────────────────────────────────────────────────────
    const deleteLog = function (row) {
        if (!row) return;
        engine.delete_log(row.id, function (data) {
            if (data && data.code === 0) {
                logs.value = logs.value.filter((l) => l.id !== row.id);
                showMessage("Log supprimé.", "success");
            } else {
                showMessage("Échec de la suppression du log.", "error");
            }
        });
    };

    // ── Helpers d'affichage ───────────────────────────────────────────────────
    const parseDate = function (v) {
        if (v === undefined || v === null || v === "") return null;
        // Timestamp numérique (ms ou s)
        if (!isNaN(v) && String(v).trim() !== "") {
            let n = parseInt(v);
            if (n < 1e12) n = n * 1000; // secondes → ms
            const d = new Date(n);
            if (!isNaN(d.getTime())) return d;
        }
        // Formats FileMaker courants
        const formats = [
            "MM/DD/YYYY HH:mm:ss", "MM/DD/YYYY HH:mm", "DD/MM/YYYY HH:mm:ss",
            "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss",
            "MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD",
        ];
        const m = moment(v, formats, true);
        if (m.isValid()) return m.toDate();
        const m2 = moment(new Date(v));
        return m2.isValid() ? m2.toDate() : null;
    };

    const formatTs = function (v) {
        const d = parseDate(v);
        if (!d) return v || "";
        return moment(d).format("DD/MM/YYYY HH:mm:ss");
    };

    const normalizeLevel = function (niveau) {
        return (niveau || "").toString().trim().toUpperCase();
    };

    const levelColor = function (niveau) {
        const n = normalizeLevel(niveau);
        if (n.indexOf("ERR") === 0 || n === "FATAL" || n === "CRITICAL") return "#EF5350";
        if (n.indexOf("WARN") === 0 || n === "ATTENTION") return "#FB8C00";
        if (n === "INFO" || n === "SUCCESS" || n === "OK") return "#43A047";
        if (n.indexOf("DEBUG") === 0 || n === "TRACE") return "#78909C";
        return "#90A4AE";
    };

    const statutColor = function (statut) {
        const n = parseInt(statut);
        if (isNaN(n)) return "#90A4AE";
        if (n < 300) return "#43A047";
        if (n < 400) return "#5C6BC0";
        if (n < 500) return "#FB8C00";
        return "#EF5350";
    };

    const isError = function (l) {
        const n = normalizeLevel(l.niveau);
        const s = parseInt(l.statut);
        return (
            n.indexOf("ERR") === 0 || n === "FATAL" || n === "CRITICAL" ||
            (!isNaN(s) && s >= 500) ||
            (l.erreur_message && l.erreur_message.length > 0)
        );
    };
    const isWarning = function (l) {
        const n = normalizeLevel(l.niveau);
        const s = parseInt(l.statut);
        return n.indexOf("WARN") === 0 || n === "ATTENTION" || (!isNaN(s) && s >= 400 && s < 500);
    };

    // ── Filtrage côté client ──────────────────────────────────────────────────
    const filteredLogs = computed(() => {
        let list = logs.value.slice();
        const f = filters.value;

        // Recherche plein texte (sur les champs utiles)
        if (f.search && f.search.trim().length > 0) {
            const q = f.search.trim().toUpperCase();
            list = list.filter((l) => {
                const hay = [
                    l.request, l.path, l.client, l.user, l.statut, l.niveau,
                    l.code_fm, l.id_correlation, l.erreur_message, l.body, l.response,
                    l.id_parcours, l.type_evenement,
                ].join(" ").toUpperCase();
                return hay.indexOf(q) !== -1;
            });
        }

        // Niveau (multi)
        if (f.niveau && f.niveau.length > 0) {
            const wanted = f.niveau.map((n) => n.toUpperCase());
            list = list.filter((l) => wanted.includes(normalizeLevel(l.niveau)));
        }

        // Type d'événement (multi : api / page / événements front…)
        if (f.typeEvenement && f.typeEvenement.length > 0) {
            const wantedT = f.typeEvenement.map((t) => (t || "").toString().toUpperCase());
            list = list.filter((l) => wantedT.includes((l.type_evenement || "").toUpperCase()));
        }

        // Session (id_parcours)
        if (f.parcours) {
            list = list.filter((l) => l.id_parcours === f.parcours);
        }

        // Statut (préfixe : "5" matche 5xx, "404" matche 404)
        if (f.statut && String(f.statut).trim().length > 0) {
            const s = String(f.statut).trim();
            list = list.filter((l) => String(l.statut).indexOf(s) === 0);
        }

        // Site : on compare au champ `domaine` (fiable pour TOUS les types) et,
        // à défaut (anciens logs sans domaine), à l'hôte extrait du path.
        if (f.site) {
            list = list.filter((l) => (l.domaine || siteHost(l.path)) === f.site);
        }

        // Client (IP)
        if (f.client) {
            list = list.filter((l) => clientIp(l.client) === f.client);
        }

        // User (regroupé par code : "C165810" == "C165810 · nom · email").
        // "(Utilisateur inconnu)" → appels sans utilisateur.
        if (f.user) {
            if (f.user === UNKNOWN_USER) {
                list = list.filter((l) => !((l.user || "").toString().trim()));
            } else {
                const uc = userCode(f.user).toUpperCase();
                list = list.filter((l) => userCode(l.user).toUpperCase() === uc);
            }
        }

        // Plage de dates (sur l'horodatage de création)
        if (f.dateFrom) {
            const from = parseDate(f.dateFrom);
            if (from) list = list.filter((l) => {
                const d = parseDate(l.horodatage);
                return d ? d.getTime() >= from.getTime() : true;
            });
        }
        if (f.dateTo) {
            const to = parseDate(f.dateTo);
            if (to) {
                // fin de journée incluse
                to.setHours(23, 59, 59, 999);
                list = list.filter((l) => {
                    const d = parseDate(l.horodatage);
                    return d ? d.getTime() <= to.getTime() : true;
                });
            }
        }

        // Tri : plus récent en premier
        list.sort((a, b) => {
            const da = parseDate(a.horodatage);
            const db = parseDate(b.horodatage);
            return (db ? db.getTime() : 0) - (da ? da.getTime() : 0);
        });

        // Fenêtre glissante (preset de période) appliquée AUSSI côté client :
        // on n'affiche jamais de log plus ancien que la fenêtre demandée, même
        // si le serveur en a renvoyé davantage. (Le "custom" est déjà géré par
        // les filtres dateFrom/dateTo ci-dessus.)
        let hasWindow = false;
        if (f.periode && f.periode !== "custom" && PERIODE_MS[f.periode]) {
            const start = Date.now() - PERIODE_MS[f.periode];
            list = list.filter((l) => { const d = parseDate(l.horodatage); return d ? d.getTime() >= start : false; });
            hasWindow = true;
        } else if (f.periode === "custom" && (f.dateFrom || f.dateTo)) {
            hasWindow = true;
        }

        // Nombre de lignes : la PÉRIODE prime. S'il y a une fenêtre active, on
        // n'applique PAS le plafond (on montre tout ce qui est dans la fenêtre).
        // Sinon, on limite au "Nb lignes" (les plus récentes).
        if (!hasWindow) {
            let lim = parseInt(filters.value.limit);
            if (!isNaN(lim) && lim > 0 && list.length > lim) list = list.slice(0, lim);
        }

        return list;
    });

    // ── KPI (calculés à partir des logs filtrés, sans dépendre du back) ────────
    const stats = computed(() => {
        const list = filteredLogs.value;
        let errors = 0, warnings = 0, durSum = 0, durCount = 0;
        list.forEach((l) => {
            if (isError(l)) errors++;
            else if (isWarning(l)) warnings++;
            const d = parseInt(l.duree_ms);
            if (!isNaN(d)) { durSum += d; durCount++; }
        });
        return {
            total: list.length,
            errors: errors,
            warnings: warnings,
            avgDuration: durCount > 0 ? Math.round(durSum / durCount) : 0,
        };
    });

    // ── Clients actifs (sur les logs filtrés : période + filtres en cours) ───
    // Client = code utilisateur (partie avant « · »). Une session (id_parcours)
    // où un utilisateur apparaît lui est rattachée ; les sessions sans aucun
    // utilisateur comptent comme visites non identifiées.
    const activeClients = computed(() => {
        const byCode = {};
        const sessions = {};
        const sessionsWithUser = {};
        const ips = {};
        filteredLogs.value.forEach((l) => {
            const s = (l.id_parcours || "").toString().trim();
            if (s) sessions[s] = true;
            const ip = clientIp(l.client);
            if (ip) ips[ip] = true;
            const u = (l.user || "").toString().trim();
            if (!u) return;
            const code = userCode(u).toUpperCase();
            if (s) sessionsWithUser[s] = true;
            const d = parseDate(l.horodatage);
            const ms = d ? d.getTime() : 0;
            const c = byCode[code] || (byCode[code] = { code: code, label: u, count: 0, last: 0, sessions: {} });
            c.count++;
            if (u.length > c.label.length) c.label = u; // libellé le plus complet (nom, email)
            if (ms > c.last) c.last = ms;
            if (s) c.sessions[s] = true;
        });
        const list = Object.keys(byCode)
            .map((k) => ({ code: byCode[k].code, label: byCode[k].label, count: byCode[k].count, last: byCode[k].last, sessions: Object.keys(byCode[k].sessions).length }))
            .sort((a, b) => b.last - a.last);
        const allSessions = Object.keys(sessions);
        return {
            count: list.length,
            list: list,
            sessions: allSessions.length,
            anonSessions: allSessions.filter((s) => !sessionsWithUser[s]).length,
            ips: Object.keys(ips).length,
        };
    });
    // « il y a 5 min », « il y a 2 h », sinon date courte
    const sinceLabel = function (ms) {
        if (!ms) return "";
        const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
        if (s < 60) return "à l'instant";
        const m = Math.round(s / 60);
        if (m < 60) return "il y a " + m + " min";
        const h = Math.round(m / 60);
        if (h < 24) return "il y a " + h + " h";
        return moment(ms).format("DD/MM HH:mm");
    };

    // Niveaux présents (pour proposer le filtre dynamiquement)
    const levelOptions = computed(() => {
        const set = {};
        logs.value.forEach((l) => { const n = normalizeLevel(l.niveau); if (n) set[n] = true; });
        const found = Object.keys(set);
        // Valeurs par défaut si rien encore chargé
        const base = ["INFO", "WARN", "ERROR", "DEBUG"];
        base.forEach((b) => { if (!set[b]) found.push(b); });
        return [...new Set(found)];
    });

    // ── Helpers d'affichage compact (pour ne pas déformer le tableau) ─────────
    // Le champ "client" contient IP(s) + User-Agent, séparés par " · ".
    const parseClient = function (client) {
        const raw = (client || "").toString();
        if (!raw) return { ip: "", agent: "" };
        const idx = raw.indexOf(" · ");
        if (idx === -1) {
            // Pas de séparateur : on tente de couper avant "Mozilla".
            const m = raw.indexOf("Mozilla");
            if (m > 0) return { ip: raw.slice(0, m).trim().replace(/,\s*$/, ""), agent: raw.slice(m).trim() };
            return { ip: raw.trim(), agent: "" };
        }
        return { ip: raw.slice(0, idx).trim(), agent: raw.slice(idx + 3).trim() };
    };

    // IP unique et lisible (déduplique "x, x").
    const clientIp = function (client) {
        const ip = parseClient(client).ip;
        if (!ip) return "";
        const parts = ip.split(",").map((p) => p.trim()).filter((p) => p.length > 0);
        return [...new Set(parts)].join(", ");
    };

    // Navigateur + OS résumés à partir du User-Agent.
    const clientAgentShort = function (client) {
        const ua = parseClient(client).agent;
        if (!ua) return "";
        let browser = "";
        if (/Edg/i.test(ua)) browser = "Edge";
        else if (/OPR|Opera/i.test(ua)) browser = "Opera";
        else if (/Chrome/i.test(ua)) browser = "Chrome";
        else if (/Firefox/i.test(ua)) browser = "Firefox";
        else if (/Safari/i.test(ua)) browser = "Safari";
        let os = "";
        if (/iPhone/i.test(ua)) os = "iPhone";
        else if (/iPad/i.test(ua)) os = "iPad";
        else if (/Android/i.test(ua)) os = "Android";
        else if (/Mac OS X|Macintosh/i.test(ua)) os = "macOS";
        else if (/Windows/i.test(ua)) os = "Windows";
        else if (/Linux/i.test(ua)) os = "Linux";
        return [browser, os].filter((v) => v).join(" · ");
    };

    // Hôte (site) extrait du champ path (ex : https://velo-test2.accen.fr/... → velo-test2.accen.fr)
    const siteHost = function (path) {
        const raw = (path || "").toString();
        const m = raw.match(/https?:\/\/([^/\s,]+)/i);
        if (m && m[1]) return m[1];
        return raw.length > 40 ? raw.slice(0, 40) + "…" : raw;
    };

    // Méthode HTTP extraite du champ request (GET/POST/PUT/DELETE…).
    const requestMethod = function (request) {
        const m = (request || "").toString().trim().match(/^(GET|POST|PUT|PATCH|DELETE|OPTIONS|HEAD)/i);
        return m ? m[1].toUpperCase() : "";
    };
    // Reste de la requête (route) sans la méthode.
    const requestPath = function (request) {
        const raw = (request || "").toString().trim();
        const method = requestMethod(request);
        return method ? raw.slice(method.length).trim() : raw;
    };
    const methodColor = function (request) {
        const m = requestMethod(request);
        if (m === "GET") return "#43A047";
        if (m === "POST") return "#5C6BC0";
        if (m === "PUT" || m === "PATCH") return "#FB8C00";
        if (m === "DELETE") return "#EF5350";
        return "#90A4AE";
    };

    // ── Listes déroulantes (valeurs distinctes présentes dans les logs) ───────
    const siteOptions = computed(() => {
        const set = {};
        logs.value.forEach((l) => { const s = l.domaine || siteHost(l.path); if (s) set[s] = true; });
        return Object.keys(set).sort();
    });
    const clientOptions = computed(() => {
        const set = {};
        logs.value.forEach((l) => { const c = clientIp(l.client); if (c) set[c] = true; });
        return Object.keys(set).sort();
    });
    // Code utilisateur = partie avant " · " (ex. "C165810" dans
    // "C165810 · betta · christian.betta@accen.fr"). Sert à regrouper le même
    // utilisateur, qu'il soit journalisé sous sa forme courte ou complète.
    const userCode = function (user) {
        const raw = (user || "").toString().trim();
        if (!raw) return "";
        const idx = raw.indexOf(" · ");
        return (idx === -1 ? raw : raw.slice(0, idx)).trim();
    };
    // Libellé le plus complet connu pour un utilisateur (parmi les logs chargés).
    const userDisplay = function (user) {
        const code = userCode(user).toUpperCase();
        let best = (user || "").toString().trim();
        logs.value.forEach((l) => {
            const u = (l.user || "").toString().trim();
            if (u && userCode(u).toUpperCase() === code && u.length > best.length) best = u;
        });
        return best;
    };
    const userOptions = computed(() => {
        // Une seule entrée par code utilisateur → on garde le libellé le plus
        // complet (avec nom/email si présent). Les logs SANS utilisateur sont
        // regroupés sous une entrée spéciale "(Utilisateur inconnu)".
        const best = {};
        let hasUnknown = false;
        logs.value.forEach((l) => {
            const u = (l.user || "").toString().trim();
            if (!u) { hasUnknown = true; return; }
            const k = userCode(u).toUpperCase();
            if (!best[k] || u.length > best[k].length) best[k] = u;
        });
        const arr = Object.keys(best).map((k) => best[k]).sort();
        if (hasUnknown) arr.unshift(UNKNOWN_USER);
        return arr;
    });
    // Types d'événement présents (api / page / événements front…).
    // Types d'événement réellement présents dans les logs chargés (aucune
    // valeur en dur : uniquement ce qui est trouvé).
    const typeEvenementOptions = computed(() => {
        const set = {};
        logs.value.forEach((l) => { if (l.type_evenement) set[l.type_evenement] = true; });
        return Object.keys(set).sort();
    });
    // Sessions (id_parcours) présentes dans les logs chargés.
    const parcoursOptions = computed(() => {
        const set = {};
        logs.value.forEach((l) => { if (l.id_parcours) set[l.id_parcours] = true; });
        return Object.keys(set).sort();
    });

    // Couleur associée à un type d'événement (chip).
    const typeEvenementColor = function (type) {
        const t = (type || "").toString().trim().toLowerCase();
        if (t === "api") return "#5C6BC0";              // appel backend
        if (t === "page") return "#26A69A";             // navigation / page
        if (t === "clic" || t === "click" || t === "bouton" || t === "button") return "#FB8C00";
        if (t === "front" || t === "event" || t === "evenement" || t === "action") return "#AB47BC";
        if (!t) return "#90A4AE";
        return "#78909C";
    };

    // ── Init ───────────────────────────────────────────────────────────────
    // On ne charge PAS les logs au démarrage : on récupère seulement la liste
    // des sites. Les logs se chargent une fois un site sélectionné.
    loadSites();

    // Dès qu'un site est choisi (ou changé), on (re)charge ses logs.
    watch(() => filters.value.site, (val) => {
        if (val) loadLogs();
        else logs.value = [];
    });

    // Le TYPE d'événement et la SESSION (parcours) sont des dimensions « serveur » :
    // pour suivre tout ce qu'a fait un utilisateur, il faut aller rechercher les
    // événements (page/selection…) qui ne sont pas forcément dans les dernières
    // lignes chargées. Donc dès qu'on change l'un d'eux → rechargement serveur.
    watch(
        () => (filters.value.parcours || "") + "|" + (filters.value.typeEvenement || []).join(","),
        () => { if (filters.value.site) loadLogs(); }
    );

    // ── Mode LIVE ────────────────────────────────────────────────────────────
    // Clé unique d'une ligne (pour dédupliquer lors des fusions).
    const logKey = (l) => (l.recordId || l.id || "") + "#" + (l.horodatage || "");

    // Fusionne les nouvelles lignes SANS remplacer le tableau : on ajoute
    // uniquement celles qu'on n'a pas déjà, on retrie par date, on plafonne la
    // mémoire. filteredLogs se charge du tri/affichage/limite à l'écran.
    const mergeLogs = function (fresh) {
        if (!fresh || !fresh.length) return;
        const seen = {};
        logs.value.forEach((l) => { seen[logKey(l)] = true; });
        const toAdd = fresh.filter((l) => !seen[logKey(l)]);
        if (!toAdd.length) return;
        const addedIds = toAdd.map((l) => l.id);
        let merged = toAdd.concat(logs.value);
        merged.sort((a, b) => {
            const da = parseDate(a.horodatage), db = parseDate(b.horodatage);
            return (db ? db.getTime() : 0) - (da ? da.getTime() : 0);
        });
        const CAP = 2000;
        if (merged.length > CAP) merged = merged.slice(0, CAP);
        logs.value = merged;
        // Surlignage PERSISTANT : seules les lignes du DERNIER lot gardent le
        // fond vert. Au lot suivant, le surlignage passe aux nouvelles arrivées.
        newIds.value = addedIds;
    };

    // Un "tick" live : ne demande QUE ce qui est plus récent que le dernier log
    // connu (dateFrom = dernier horodatage) → charge très léger, et silencieux
    // (ne touche pas isLoading, donc le tableau ne disparaît jamais).
    const liveTick = function () {
        if (!filters.value.site) return;
        const f = buildBackendFilters();
        let lastTs = 0;
        logs.value.forEach((l) => { const d = parseDate(l.horodatage); if (d) lastTs = Math.max(lastTs, d.getTime()); });
        // On redemande à partir du dernier horodatage connu (et pas +1 s) pour
        // ne jamais rater une ligne arrivée dans la même seconde : les doublons
        // sont écartés par mergeLogs. Plus réactif.
        if (lastTs > 0) { f.dateFrom = lastTs; f.dateTo = null; }
        f.limit = 500; // marge en cas de pic d'activité entre deux ticks
        // Endpoint LIVE dédié : réutilise la session FileMaker côté serveur
        // (pas de login/logout à chaque tick) → charge FileMaker réduite.
        engine.get_logs_live(f, function (data) {
            if (!data || data.code === -1) return; // silencieux : on ne casse rien
            mergeLogs(normalizeRecords(data));
        });
    };

    let liveTimer = null;
    const startLive = function () { stopLive(); liveTick(); liveTimer = setInterval(liveTick, LIVE_INTERVAL_MS); };
    const stopLive = function () { if (liveTimer) { clearInterval(liveTimer); liveTimer = null; } };
    watch(isLive, (v) => { if (v && filters.value.site) startLive(); else { stopLive(); newIds.value = []; } });
    // Si on change de site pendant le live, on relance proprement (après le
    // rechargement initial du nouveau site).
    watch(() => filters.value.site, (v) => { if (!v) stopLive(); else if (isLive.value) startLive(); });
    onBeforeUnmount(stopLive);

    return {
        // state
        logs,
        isLoading,
        isSitesLoading,
        sitesError,
        filters,
        siteList,
        siteOptions,
        clientOptions,
        userOptions,
        typeEvenementOptions,
        parcoursOptions,
        levelOptions,
        selectedLog,
        isDetailOpen,
        autoRefresh,
        isLive,
        newIds,
        snackbar,
        // derived
        filteredLogs,
        stats,
        activeClients,
        // actions
        loadLogs,
        loadLogsAll,
        loadSites,
        refresh,
        resetFilters,
        changeSite,
        openDetail,
        closeDetail,
        deleteLog,
        // helpers
        formatTs,
        levelColor,
        statutColor,
        isError,
        isWarning,
        clientIp,
        clientAgentShort,
        siteHost,
        requestMethod,
        requestPath,
        methodColor,
        typeEvenementColor,
        userDisplay,
        sinceLabel,
    };
}
